import React, { useState } from 'react';
import { ArrowLeft, Camera } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const SecondarySurvey = ({ onNext, onBack, onLocalBurnAssessment, onDataChange, data }) => {
  const [ampleData, setAmpleData] = useState(data?.ample || {
    allergies: '',
    medications: '',
    pastMedical: '',
    lastMeal: '',
    events: ''
  });

  const [physicalExam, setPhysicalExam] = useState(data?.physical || {
    generalSurvey: '',
    associatedInjuries: '',
    specialConsiderations: ''
  });

  const handleAmpleChange = (field, value) => {
    const newData = { ...ampleData, [field]: value };
    setAmpleData(newData);
    if (onDataChange) {
      onDataChange({ secondary_survey_ample: newData });
    }
  };

  const handlePhysicalChange = (field, value) => {
    const newData = { ...physicalExam, [field]: value };
    setPhysicalExam(newData);
    if (onDataChange) {
      onDataChange({ secondary_survey_physical: newData });
    }
  };

  const handleNext = () => {
    if (onDataChange) {
      onDataChange({ 
        secondary_survey_ample: ampleData,
        secondary_survey_physical: physicalExam
      });
    }
    onNext();
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-black py-4">
      <div className="w-full max-w-sm mx-auto px-4">
        <div className="flex flex-col min-h-screen bg-zinc-900 rounded-3xl border-2 border-orange-500/30 shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b-2 border-orange-500/30">
            <Button onClick={onBack} variant="ghost" className="flex items-center gap-2 text-orange-500 text-lg font-bold">
              <ArrowLeft className="w-6 h-6" /> Back
            </Button>
            <h1 className="text-lg font-black text-orange-500">Secondary Survey</h1>
            <div className="w-16" />
          </div>

          <div className="flex-grow overflow-y-auto bg-black p-4 space-y-5">
            {/* History - AMPLE */}
            <section>
              <h2 className="text-2xl font-black mb-4 text-orange-400">History - AMPLE</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-lg font-bold text-orange-300 mb-2">Allergies</label>
                  <Input
                    value={ampleData.allergies}
                    onChange={(e) => handleAmpleChange('allergies', e.target.value)}
                    placeholder="List allergies"
                    className="w-full rounded-xl bg-zinc-900 border-2 border-orange-500/30 text-base text-orange-100 p-3"
                  />
                </div>
                <div>
                  <label className="block text-lg font-bold text-orange-300 mb-2">Medications</label>
                  <Input
                    value={ampleData.medications}
                    onChange={(e) => handleAmpleChange('medications', e.target.value)}
                    placeholder="Current medications"
                    className="w-full rounded-xl bg-zinc-900 border-2 border-orange-500/30 text-base text-orange-100 p-3"
                  />
                </div>
                <div>
                  <label className="block text-lg font-bold text-orange-300 mb-2">Past Medical History</label>
                  <textarea
                    value={ampleData.pastMedical}
                    onChange={(e) => handleAmpleChange('pastMedical', e.target.value)}
                    placeholder="Medical history"
                    rows="2"
                    className="w-full rounded-xl bg-zinc-900 border-2 border-orange-500/30 text-base text-orange-100 p-3"
                  />
                </div>
                <div>
                  <label className="block text-lg font-bold text-orange-300 mb-2">Last Meal</label>
                  <Input
                    value={ampleData.lastMeal}
                    onChange={(e) => handleAmpleChange('lastMeal', e.target.value)}
                    placeholder="Time and content"
                    className="w-full rounded-xl bg-zinc-900 border-2 border-orange-500/30 text-base text-orange-100 p-3"
                  />
                </div>
                <div>
                  <label className="block text-lg font-bold text-orange-300 mb-2">Events Leading to Injury</label>
                  <textarea
                    value={ampleData.events}
                    onChange={(e) => handleAmpleChange('events', e.target.value)}
                    placeholder="Describe the events"
                    rows="2"
                    className="w-full rounded-xl bg-zinc-900 border-2 border-orange-500/30 text-base text-orange-100 p-3"
                  />
                </div>
              </div>
            </section>

            {/* Local Burn Assessment - Clickable */}
            <section>
              <div 
                onClick={onLocalBurnAssessment}
                className="rounded-2xl bg-yellow-900/40 border-2 border-yellow-500 p-5 cursor-pointer hover:bg-yellow-900/50 transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <Camera className="w-8 h-8 text-yellow-400" />
                  <h2 className="text-xl font-black text-yellow-100">Local Burn Assessment</h2>
                </div>
                <p className="text-base text-yellow-200 leading-relaxed">
                  Click to perform TBSA estimation and burn photography
                </p>
              </div>
            </section>

            {/* Physical Examination */}
            <section>
              <h2 className="text-2xl font-black mb-4 text-orange-400">Physical Examination</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-lg font-bold text-orange-300 mb-2">General Survey</label>
                  <textarea
                    value={physicalExam.generalSurvey}
                    onChange={(e) => handlePhysicalChange('generalSurvey', e.target.value)}
                    placeholder="Overall appearance, vital signs"
                    rows="2"
                    className="w-full rounded-xl bg-zinc-900 border-2 border-orange-500/30 text-base text-orange-100 p-3"
                  />
                </div>
                <div>
                  <label className="block text-lg font-bold text-orange-300 mb-2">Associated Injuries</label>
                  <textarea
                    value={physicalExam.associatedInjuries}
                    onChange={(e) => handlePhysicalChange('associatedInjuries', e.target.value)}
                    placeholder="Fractures, head injuries, other trauma"
                    rows="2"
                    className="w-full rounded-xl bg-zinc-900 border-2 border-orange-500/30 text-base text-orange-100 p-3"
                  />
                </div>
                <div>
                  <label className="block text-lg font-bold text-orange-300 mb-2">Special Considerations</label>
                  <textarea
                    value={physicalExam.specialConsiderations}
                    onChange={(e) => handlePhysicalChange('specialConsiderations', e.target.value)}
                    placeholder="Age, pre-existing conditions"
                    rows="2"
                    className="w-full rounded-xl bg-zinc-900 border-2 border-orange-500/30 text-base text-orange-100 p-3"
                  />
                </div>
              </div>
            </section>
          </div>

          <div className="p-4 border-t-2 border-orange-500/30 bg-zinc-900">
            <Button onClick={handleNext} className="w-full h-14 bg-orange-500 text-white text-lg font-black rounded-xl hover:bg-orange-600 shadow-xl">
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecondarySurvey;